import React, { useState } from "react";
import dayjs from "dayjs";
import FilterDropdown from "../../components/ui-components/FilterDropdown";

// Period-first selector for finance users: named fiscal-ish periods as the primary
// control, with a "Custom range…" escape hatch that reveals two date inputs for the
// analyst who needs an exact window. Underneath it still drives the store's two dates,
// so everything downstream (spend/attribution/forecast) is unchanged.
export const PRESETS = [
  { value: "this_month", label: "This month" },
  { value: "last_month", label: "Last month" },
  { value: "mtd", label: "Month to date" },
  { value: "last_30", label: "Last 30 days" },
  { value: "this_quarter", label: "This quarter" },
  { value: "qtd", label: "Quarter to date" },
  { value: "this_year", label: "This year" },
  { value: "ytd", label: "Year to date" },
  { value: "last_year", label: "Last year" },
  { value: "custom", label: "Custom range…" },
];

// Quarter start month without the dayjs quarterOfYear plugin (not guaranteed loaded).
const qStart = (d) => d.month(Math.floor(d.month() / 3) * 3).startOf("month");

// rangeFor returns [start, end] dayjs for a preset relative to `now`. null for custom.
export function rangeFor(preset, now = dayjs()) {
  switch (preset) {
    case "this_month": return [now.startOf("month"), now.endOf("month")];
    case "last_month": { const m = now.subtract(1, "month"); return [m.startOf("month"), m.endOf("month")]; }
    case "mtd": return [now.startOf("month"), now.endOf("day")];
    case "last_30": return [now.subtract(29, "day").startOf("day"), now.endOf("day")];
    case "this_quarter": return [qStart(now), qStart(now).add(2, "month").endOf("month")];
    case "qtd": return [qStart(now), now.endOf("day")];
    case "this_year": return [now.startOf("year"), now.endOf("year")];
    case "ytd": return [now.startOf("year"), now.endOf("day")];
    case "last_year": { const y = now.subtract(1, "year"); return [y.startOf("year"), y.endOf("year")]; }
    default: return null;
  }
}

// matchPreset returns the preset whose range equals [start,end] at day granularity, else null.
export function matchPreset(start, end, now = dayjs()) {
  for (const p of PRESETS) {
    if (p.value === "custom") continue;
    const r = rangeFor(p.value, now);
    if (r && r[0].isSame(start, "day") && r[1].isSame(end, "day")) return p.value;
  }
  return null;
}

export default function AnalyzerPeriodSelector({ startDate, endDate, setStartDate, setEndDate, variant = "outline" }) {
  // Once the user explicitly picks "Custom range…" we stay in custom even if the dates
  // happen to still match a preset; picking any preset exits custom.
  const [customMode, setCustomMode] = useState(false);
  const active = customMode ? "custom" : (matchPreset(startDate, endDate) || "custom");

  const onPreset = (value) => {
    if (value === "custom") { setCustomMode(true); return; }
    setCustomMode(false);
    const r = rangeFor(value);
    if (r) { setStartDate(r[0]); setEndDate(r[1]); }
  };

  return (
    <div className="flex items-center gap-2">
      <FilterDropdown value={active} onChange={onPreset} options={PRESETS} ariaLabel="Period" minWidth={150} height={32} menuMaxHeight={440} variant={variant} />
      {active === "custom" && (
        <div className="flex items-center gap-1 text-[13px]">
          <input
            type="date"
            aria-label="From"
            value={startDate.format("YYYY-MM-DD")}
            onChange={(e) => setStartDate(dayjs(e.target.value).startOf("day"))}
            style={{ height: 38 }}
            className="box-border rounded-ctrl border border-solid border-card-bd bg-card px-2 text-[13px] text-text focus:outline-none focus:ring-1 focus:ring-primary"
          />
          <span className="text-text-3">→</span>
          <input
            type="date"
            aria-label="To"
            value={endDate.format("YYYY-MM-DD")}
            onChange={(e) => setEndDate(dayjs(e.target.value).endOf("day"))}
            style={{ height: 38 }}
            className="box-border rounded-ctrl border border-solid border-card-bd bg-card px-2 text-[13px] text-text focus:outline-none focus:ring-1 focus:ring-primary"
          />
        </div>
      )}
    </div>
  );
}
